import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import {
  ForgeVariantReviewPayload,
  SectionTarget,
} from '../../types';
import { UPCColumnView, FocusedHighlightInfo } from '../../components/common/UPCColumnView';
import { CropActionBar } from '../../components/common/CropActionBar';
import { TaskGuideOverlay, TYPE_LABELS, TYPE_COLORS } from '../../components/common/TaskGuideOverlay';
import { useUPCActions, PendingChanges } from '../../hooks/useUPCActions';
import { useNotification } from '../../hooks/useNotification';
import { useZoomPreview } from '../../hooks/useZoomPreview';

// ── Types ──

type HighlightStatus = 'pending' | 'accepted' | 'dismissed';

// ── Component ──

interface VariantReviewUIProps {
  payload: ForgeVariantReviewPayload;
  onSelect: (data: PendingChanges) => void;
}

export const VariantReviewUI: React.FC<VariantReviewUIProps> = ({ payload, onSelect }) => {
  const { notify, NotificationToast } = useNotification();

  const [highlightStatuses, setHighlightStatuses] = useState<Map<string, HighlightStatus>>(
    () => new Map(payload.highlights.map(h => [h.id, 'pending']))
  );
  const [focusedHighlight, setFocusedHighlight] = useState<string | null>(
    () => payload.highlights.length > 0 ? payload.highlights[0].id : null
  );
  const [showInfoPopover, setShowInfoPopover] = useState(false);
  const { handleImageHover, handleImageMove, handleImageLeave, ZoomOverlay } = useZoomPreview();

  const actions = useUPCActions({
    initialUpcData: payload.upcs,
    notify,
    onChangesUpdate: onSelect,
    saveUndoExtra: () => new Map(highlightStatuses),
    restoreUndoExtra: (extra) => setHighlightStatuses(extra as Map<string, HighlightStatus>),
  });

  // ESC / Undo keyboard
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showInfoPopover) { setShowInfoPopover(false); return; }
        actions.setSelectedCropIds(new Set());
        actions.setMergeSourceVariantId(null);
        setFocusedHighlight(null);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        actions.undo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [actions, showInfoPopover]);

  // ── Highlight navigation ──

  const advanceHighlight = useCallback((fromId: string, newStatuses: Map<string, HighlightStatus>) => {
    const idx = payload.highlights.findIndex(h => h.id === fromId);
    for (let i = 1; i <= payload.highlights.length; i++) {
      const next = payload.highlights[(idx + i) % payload.highlights.length];
      if (next && newStatuses.get(next.id) === 'pending') {
        return next.id;
      }
    }
    return null;
  }, [payload.highlights]);

  // Highlight keyboard controls
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!focusedHighlight || actions.mode !== 'idle') return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        const idx = payload.highlights.findIndex(hl => hl.id === focusedHighlight);
        setFocusedHighlight(payload.highlights[(idx + 1) % payload.highlights.length].id);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        const idx = payload.highlights.findIndex(hl => hl.id === focusedHighlight);
        setFocusedHighlight(payload.highlights[(idx - 1 + payload.highlights.length) % payload.highlights.length].id);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [focusedHighlight, actions.mode, payload.highlights]);

  // ── Computed ──

  const highlightProgress = useMemo(() => {
    const total = payload.highlights.length;
    let done = 0;
    for (const status of highlightStatuses.values()) {
      if (status !== 'pending') done++;
    }
    return { done, total };
  }, [highlightStatuses, payload.highlights]);

  const focusedHighlightData = useMemo((): FocusedHighlightInfo | null => {
    if (!focusedHighlight) return null;
    const h = payload.highlights.find(h => h.id === focusedHighlight);
    if (!h) return null;

    let cropItemNumber: string | undefined;
    if (h.cropId) {
      const loc = actions.findCropLocation(h.cropId, actions.upcData);
      if (loc) cropItemNumber = actions.upcData[loc.upcIdx].itemNumber;
    }

    return { ...h, cropItemNumber };
  }, [focusedHighlight, payload.highlights, actions]);

  // ── Auto-complete section-level highlights ──

  useEffect(() => {
    let changed = false;
    const next = new Map(highlightStatuses);
    for (const h of payload.highlights) {
      if (next.get(h.id) !== 'pending') continue;
      if (h.type === 'assign_variant') {
        const allEmpty = actions.upcData.every(u => u.unassignedCrops.length === 0);
        if (allEmpty) { next.set(h.id, 'accepted'); changed = true; }
      } else if (h.type === 'clear_pending') {
        const noPending = actions.upcData.every(u => !u.variants.some(v => v.barcodeStatus === 'pending'));
        if (noPending) { next.set(h.id, 'accepted'); changed = true; }
      }
    }
    if (changed) {
      setHighlightStatuses(next);
      if (focusedHighlight && next.get(focusedHighlight) === 'accepted') {
        setFocusedHighlight(advanceHighlight(focusedHighlight, next));
      }
    }
  }, [actions.upcData, payload.highlights, highlightStatuses, focusedHighlight, advanceHighlight]);

  // ── Highlight check helper ──

  const isSectionHighlighted = useCallback((sectionKey: string, target: SectionTarget, cropCount?: number): boolean => {
    if (!focusedHighlightData) return false;
    const fh = payload.highlights.find(h => h.id === focusedHighlight);
    if (!fh) return false;
    const status = highlightStatuses.get(fh.id);
    if (status !== 'pending') return false;
    return !!(
      (fh.type === 'assign_variant' && sectionKey.startsWith('novar-') && (cropCount === undefined || cropCount > 0)) ||
      (fh.type === 'clear_pending' && sectionKey.startsWith('pending-') && (cropCount === undefined || cropCount > 0)) ||
      (fh.type === 'should_be_carton' && target.type === 'carton' && target.itemNumber === focusedHighlightData?.cropItemNumber) ||
      (fh.type === 'should_be_non_front' && target.type === 'non-front' && target.itemNumber === focusedHighlightData?.cropItemNumber)
    );
  }, [focusedHighlightData, focusedHighlight, highlightStatuses, payload.highlights]);

  // ── Idle content for task bar (highlight navigation) ──

  const renderIdleContent = () => {
    const total = payload.highlights.length;
    if (total === 0) return null;

    const currentHighlight = focusedHighlight
      ? payload.highlights.find(h => h.id === focusedHighlight)
      : null;
    const currentIdx = currentHighlight
      ? payload.highlights.findIndex(h => h.id === currentHighlight.id)
      : -1;
    const status = currentHighlight ? (highlightStatuses.get(currentHighlight.id) || 'pending') : 'pending';
    const isDone = status === 'accepted' || status === 'dismissed';

    const isSectionLevel = currentHighlight?.type === 'assign_variant' || currentHighlight?.type === 'clear_pending';
    const isPassive = currentHighlight?.type === 'anything_else' || isSectionLevel;
    const isStale = currentHighlight ? (
      currentHighlight.type === 'similar_crops' || currentHighlight.type === 'outlier_crops'
        ? (currentHighlight.cropIds || []).every(id => !actions.findCropLocation(id, actions.upcData))
        : isPassive
          ? false
          : currentHighlight.cropId ? !actions.findCropLocation(currentHighlight.cropId, actions.upcData) : false
    ) : false;

    const sectionRemaining = currentHighlight && isSectionLevel ? (() => {
      if (currentHighlight.type === 'assign_variant') return actions.upcData.reduce((a, u) => a + u.unassignedCrops.length, 0);
      if (currentHighlight.type === 'clear_pending') return actions.upcData.reduce((a, u) => a + u.variants.filter(v => v.barcodeStatus === 'pending').reduce((b, v) => b + v.crops.length, 0), 0);
      return 0;
    })() : 0;

    const nav = (dir: 1 | -1) => {
      const idx = currentIdx >= 0 ? (currentIdx + dir + total) % total : 0;
      setFocusedHighlight(payload.highlights[idx].id);
      setShowInfoPopover(false);
    };

    return (
      <>
        {/* Progress pips */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
          {payload.highlights.map((h) => {
            const s = highlightStatuses.get(h.id) || 'pending';
            const isActive = h.id === focusedHighlight;
            return (
              <div
                key={h.id}
                onClick={() => setFocusedHighlight(h.id)}
                style={{
                  width: isActive ? 16 : 6, height: 6,
                  borderRadius: 3, cursor: 'pointer',
                  transition: 'all 200ms ease',
                  background: s === 'accepted' ? 'var(--purple)'
                    : s === 'dismissed' ? 'var(--text-tertiary)'
                    : isActive ? 'var(--purple)' : 'var(--border)',
                }}
              />
            );
          })}
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-tertiary)', marginLeft: 4 }}>
            {highlightProgress.done}/{total}
          </span>
        </div>

        <div style={{ width: 1, height: 18, background: 'var(--border)', flexShrink: 0 }} />

        {/* Nav arrows */}
        <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
          <button onClick={() => nav(-1)} style={{
            background: 'none', border: 'none', color: 'var(--text-secondary)',
            cursor: 'pointer', padding: '0 2px', fontSize: 14, lineHeight: 1,
            display: 'flex', alignItems: 'center',
          }}>‹</button>
          <button onClick={() => nav(1)} style={{
            background: 'none', border: 'none', color: 'var(--text-secondary)',
            cursor: 'pointer', padding: '0 2px', fontSize: 14, lineHeight: 1,
            display: 'flex', alignItems: 'center',
          }}>›</button>
        </div>

        {/* Current task details */}
        {currentHighlight && (
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 10,
            opacity: isDone || isStale ? 0.45 : 1,
          }}>
            <span style={{
              fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.05em',
              color: isDone ? 'var(--text-tertiary)' : TYPE_COLORS[currentHighlight.type] || 'var(--text-secondary)',
              padding: '2px 6px', border: `1px solid ${isDone ? 'var(--border)' : TYPE_COLORS[currentHighlight.type] || 'var(--border)'}`,
              borderRadius: 'var(--radius-sm)', flexShrink: 0,
            }}>
              {TYPE_LABELS[currentHighlight.type]}
            </span>

            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-secondary)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
              {currentHighlight.type === 'anything_else' ? (
                <>Review everything — fix anything the highlights missed</>
              ) : isSectionLevel ? (
                <span style={{ color: 'var(--text-tertiary)' }}>{sectionRemaining} remaining</span>
              ) : currentHighlight.type === 'similar_crops' || currentHighlight.type === 'outlier_crops' ? (
                <>{(currentHighlight.cropIds || []).length} crops</>
              ) : (
                <>{(currentHighlight.cropId || '').slice(0, 20)}</>
              )}
            </div>

            {status === 'accepted' && <CheckCircleOutlineIcon style={{ fontSize: 12, color: 'var(--purple)', flexShrink: 0 }} />}
            {status === 'dismissed' && <CloseIcon style={{ fontSize: 12, color: 'var(--text-tertiary)', flexShrink: 0 }} />}
            {isStale && !isDone && <span style={{ fontSize: 8, color: 'var(--text-tertiary)', fontStyle: 'italic', flexShrink: 0 }}>stale</span>}
          </div>
        )}
      </>
    );
  };

  // ── Main render ──

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', minWidth: 0, position: 'relative' }}>
      {/* UPC Columns */}
      <div style={{
        flex: 1, display: 'flex', gap: 10, padding: '8px 12px',
        overflowX: 'auto', overflowY: 'hidden', alignItems: 'stretch',
        minWidth: 0, minHeight: 0,
      }}>
        {actions.upcData.map(upc => (
          <UPCColumnView
            key={upc.itemNumber}
            upc={upc}
            selectedCropIds={actions.selectedCropIds}
            onCropClick={actions.toggleCropSelection}
            onSectionClick={actions.handleSectionClick}
            mode={actions.mode}
            mergeSourceVariantId={actions.mergeSourceVariantId}
            activeVariantId={actions.activeVariantId}
            onActiveVariantChange={actions.setActiveVariantId}
            variantColorMap={actions.variantColorMap}
            focusedHighlight={focusedHighlightData}
            isSectionHighlighted={isSectionHighlighted}
            onImageHover={handleImageHover}
            onImageMove={handleImageMove}
            onImageLeave={handleImageLeave}
            showTriangles
          />
        ))}
      </div>

      {/* Task bar */}
      <CropActionBar
        mode={actions.mode}
        selectedCropIds={actions.selectedCropIds}
        newVariantOptions={actions.selectedCropItemNumber ? [{ itemNumber: actions.selectedCropItemNumber, label: 'New variant' }] : []}
        onDeactivate={actions.deactivateCrops}
        onDifferentItem={actions.markDifferentItem}
        onSetRep={actions.selectedCropVariantInfo && !actions.selectedCropVariantInfo.isAlreadyRep ? actions.selectedCropVariantInfo : null}
        onSetRepClick={actions.setRepresentative}
        onNewVariantClick={actions.createNewVariant}
        undoCount={actions.undoStack.length}
        onUndo={actions.undo}
        idleContent={renderIdleContent()}
        onShowInfo={() => setShowInfoPopover(true)}
      />

      {ZoomOverlay}
      {showInfoPopover && <TaskGuideOverlay onClose={() => setShowInfoPopover(false)} />}

      {NotificationToast}
    </div>
  );
};
